import { h, defineComponent, ref, watch } from 'vue';
import { n, t } from '@/ui/i18n/i18n';
import { box, column, level, loader, pagination, sortableNumericColumn } from '@/ui/components';
import {
  groupedInspectionList,
  groupedInspectionListExportUrl,
  SupportedExportFormat,
} from '@/services/api/resources/analytics';
import { emptyGroupedInspectionList, GroupedInspection, GroupedInspectionList } from '@/entities/analytics';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import * as qs from '@/ui/queryState';
import { download, enumToSelectOption } from '@/ui/helpers/uiHelper';
import { hasFeature, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { InspectionGroupBy } from '@/services/api/resources/analytics/types';
import { AnalyticsTable } from '@/ui/components/table/AnalyticsTable';
import {
  assigneeFilter,
  categoryFilter,
  checklistFilter,
  dateRangeFilter,
  filterContainer,
  groupFilter,
  entityItemFilter,
  entityFolderFilter,
} from '@/ui/components/filters';
import { dropdown } from '@/ui/components/controls/Dropdown';
import { SortDirection } from '@/ui/types';
import { exportDropdown } from '@/ui/components/dropdown/ExportDropdown';
import { renderForbiddenPage } from '../../error/ForbiddenPage';
import { decodeGroup, mergeEntityItems } from '../codec';
import { inspectionGroupBy } from '../dictionary';
import { useQueryFiltering } from './useQueryFilters';
import { useFilters } from './useFilters';
import { overdueInspectionsDetailPageLink } from './OverdueInspectionsDetailPage';
import { renderAnalyticsTeaserPage } from '../../teasers/AnalyticsTeaserPage';
import { OverdueInspectionsCharts } from '@/ui/pages/analytics/OverdueInspections/OverdueInspectionsCharts';

export const OverdueInspectionsPage = defineComponent({
  name: 'OverdueInspectionsPage',
  setup() {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir } = useSorting('group', SortDirection.Ascending);
    const groupBy = qs.stringEnum<InspectionGroupBy>(
      'groupBy',
      Object.values(InspectionGroupBy),
      InspectionGroupBy.Assignee,
      false,
    );
    const {
      start, end, group, entityItem, entityFolder, assignee, category, checklist, response, section, questionTitle,
    } = useQueryFiltering();

    // get list task
    const query = () => ({
      timezone: user.value.timezone,
      page: page.value,
      perPage: perPage.value,
      sort: sort.value,
      dir: dir.value,
      start: start.value,
      end: end.value,
      entityItem: mergeEntityItems(entityFolder.value, entityItem.value),
      group: group.value,
      assignee: assignee.value,
      category: category.value,
      checklist: checklist.value,
      overdue: false,
      response: response.value,
      section: section.value,
      questionTitle: questionTitle.value,
      excludeZeroOverdueInspectionCount: true,
    });
    const list = ref<GroupedInspectionList>(emptyGroupedInspectionList);
    const getList = useTask(
      () => groupedInspectionList(groupBy.value, query()),
      userList => {
        list.value = userList;
        initialLoading.value = false;
      },
    );
    watch([
      page,
      perPage,
      group,
      entityItem,
      entityFolder,
      assignee,
      category,
      checklist,
      start,
      end,
      groupBy,
      sort,
      dir,
    ], getList.run);

    // export task
    const { run: exportData } = useTask(
      (format: SupportedExportFormat | undefined) => groupedInspectionListExportUrl(
        groupBy.value,
        query(),
        format as SupportedExportFormat,
        user.value.locale,
      ),
      download,
    );

    // get filters task
    const { getFilters, entityItems, entityFolders, groups, users, categories, checklists } = useFilters();

    return function render() {
      return h('div', [
        h('div', [
          h('div', { class: 'columns legacy-wrapper' }, [
            filterContainer([
              loader('form', getFilters, [
                dateRangeFilter(start, end, resetPagination),
                entityItemFilter(entityItem, entityItems.value, resetPagination),
                entityFolderFilter(entityFolder, entityFolders.value, resetPagination),
                groupFilter(group, groups.value, resetPagination),
                assigneeFilter(assignee, users.value, resetPagination),
                categoryFilter(category, categories.value, resetPagination),
                checklistFilter(checklist, checklists.value, resetPagination),
              ]),
            ]),
            h('div', { class: 'column' }, [
              h(OverdueInspectionsCharts, {
                class: 'legacy',
                props: {
                  query: query(),
                  overdueInspectionCount: list.value.meta.overdueInspectionCount,
                  overdueInspectionPercentage: list.value.meta.overdueInspectionPercentage,
                },
              }),
              box([
                loader('div', getList, [
                  level(
                    [
                      //pageTitle(t('analytics.overdue-inspections')),
                      dropdown<InspectionGroupBy>(
                        {
                          value: groupBy.value,
                          options: Object
                            .values(InspectionGroupBy)
                            .map(enumToSelectOption(inspectionGroupBy)),
                          disabled: false,
                        },
                        (value: InspectionGroupBy) => qs.updateMultiple(() => {
                          resetPagination();
                          groupBy.value = value;
                        }),
                      ),
                    ],
                    [exportDropdown(exportData)],
                  ),
                  h(AnalyticsTable, {
                    props: {
                      list: list.value.data,
                      sort: sort.value,
                      dir: dir.value,
                      //use client side pagination if no backend pagination
                      paginated: (list.value.data.length > 0 && !list.value.meta.total),
                      perPage: perPage.value,
                      backendPagination: !!list.value.meta.total,
                    },
                    scopedSlots: {
                      default: () => [
                        column({
                          // this customKey property is needed because BTable uses the label
                          // as the primary identifier when refreshing columns, and when column
                          // labels change, it keeps adding new columns
                          customKey: 'group',
                          field: 'group',
                          label: inspectionGroupBy(groupBy.value),
                          sortable: true,
                        }, {
                          default: ({ row }: { row: GroupedInspection }) => [
                            overdueInspectionsDetailPageLink(
                              decodeGroup(row.group),
                              groupBy.value,
                              row.group,
                              {
                                start: start.value,
                                end: end.value,
                                entityItem: entityItem.value,
                                group: group.value,
                                assignee: assignee.value,
                                category: category.value,
                                checklist: checklist.value,
                              },
                            ),
                          ],
                        }),
                        sortableNumericColumn({
                          field: 'overdueInspectionCount',
                          label: t('analytics.overdue-inspections'),
                        }, {
                          default: ({ row }: { row: GroupedInspection }) =>
                            n(row.overdueInspectionCount, 'analyticsInteger'),
                        }),
                        sortableNumericColumn({
                          field: 'inspectionCount',
                          label: t('common.inspections'),
                        }, {
                          default: ({ row }: { row: GroupedInspection }) =>
                            n(row.inspectionCount, 'analyticsInteger'),
                        }),
                        sortableNumericColumn({
                          field: 'overdueInspectionPercentage',
                          label: t('analytics.overdue-count'),
                        }, {
                          default: ({ row }: { row: GroupedInspection }) =>
                            n(row.overdueInspectionPercentage, 'analyticsPercentage'),
                        }),
                      ],
                    },
                    on: {
                      sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                        resetPagination();
                        sort.value = field;
                        dir.value = direction;
                      }),
                    },
                  }),
                  (list.value.meta.total)
                    ? pagination({
                      current: page.value,
                      total: list.value.meta.total,
                      perPage: perPage.value,
                    })
                    : null,
                ], !initialLoading.value),
              ]),
            ]),
          ]),
        ]),
      ]);
    };
  },
});
